import { toCalendarDate } from '../../utils/helpers/aiSpendFormat';

/**
 * Presets plus two native date inputs.
 *
 * `<input type="date">` speaks 'YYYY-MM-DD' strings in and out, so no Date is
 * ever constructed and no toISOString() can shift the boundary a day. The
 * existing DatePicker is 947 lines, single-date and shaped for birthdates
 * (MIN_YEAR = currentYear - 150); teaching it ranges is a much larger change to
 * a component the profile forms depend on.
 */

const shiftDays = (days) => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - (days - 1));
  return { date_from: toCalendarDate(from), date_to: toCalendarDate(to) };
};

const monthToDate = () => {
  const now = new Date();
  return {
    date_from: toCalendarDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    date_to: toCalendarDate(now),
  };
};

const lastMonth = () => {
  const now = new Date();
  return {
    date_from: toCalendarDate(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
    date_to: toCalendarDate(new Date(now.getFullYear(), now.getMonth(), 0)),
  };
};

export const PRESETS = [
  ['Today', () => shiftDays(1)],
  ['7 days', () => shiftDays(7)],
  ['30 days', () => shiftDays(30)],
  ['90 days', () => shiftDays(90)],
  ['Month to date', monthToDate],
  ['Last month', lastMonth],
];

const inputClass =
  'rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:[color-scheme:dark] dark:border-slate-600 dark:bg-slate-800 dark:text-white';

export default function DateRangeControl({ value, onChange, timezone }) {
  const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const active = (preset) => {
    const range = preset();
    return range.date_from === value.date_from && range.date_to === value.date_to;
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {PRESETS.map(([label, preset]) => (
          <button
            key={label}
            type="button"
            onClick={() => onChange(preset())}
            className={`rounded-lg border px-3 py-1.5 text-sm font-medium ${
              active(preset)
                ? 'border-blue-600 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                : 'border-slate-300 text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700'
            }`}
          >
            {label}
          </button>
        ))}
        <label className="sr-only" htmlFor="ai-spend-date-from">
          From
        </label>
        <input
          id="ai-spend-date-from"
          type="date"
          className={inputClass}
          value={value.date_from}
          max={value.date_to}
          onChange={(event) => onChange({ date_from: event.target.value })}
        />
        <span className="text-slate-400">—</span>
        <label className="sr-only" htmlFor="ai-spend-date-to">
          To
        </label>
        <input
          id="ai-spend-date-to"
          type="date"
          className={inputClass}
          value={value.date_to}
          min={value.date_from}
          onChange={(event) => onChange({ date_to: event.target.value })}
        />
      </div>
      {timezone && (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Days are counted in {timezone}
          {browserZone && browserZone !== timezone
            ? `; your browser is in ${browserZone}.`
            : '.'}
        </p>
      )}
    </div>
  );
}
